/**
 * Seam Solid adapter — the blessed easy path.
 *
 * defineSolidComponent(): author with Solid reactivity (via ctx.ui.solid +
 * ctx.ui.html), get back a framework-free SeamComponent.
 * SeamHost: mounts ANY SeamComponent (foreign or adapter-made) into the app
 * tree, with shadow-root style containment by default.
 */
import type { Component } from 'solid-js'
import { createEffect, onCleanup, onMount } from 'solid-js'
import { createStore } from 'solid-js/store'
import { render } from 'solid-js/web'
import { ThreadContext, useThread } from '../../ui/reactive'
import type { PluginContext, SeamComponent, SeamProps } from './contract'
import { deriveMountCtx } from './context'

export function defineSolidComponent(
	name: string,
	fn: (props: SeamProps, ctx: PluginContext) => unknown,
	opts?: { styleIsolation?: 'shadow' | 'none' },
): SeamComponent {
	return {
		name,
		styleIsolation: opts?.styleIsolation ?? 'shadow',
		mount(el, props, ctx) {
			const [store, setStore] = createStore<SeamProps>({ ...props })
			// Re-provide ThreadContext inside the fresh Solid root, so app-native
			// components (builtins ported through the seam) keep working (round-5b/6).
			const dispose = render(() => (
				<ThreadContext.Provider value={ctx.wovin.rawThread}>
					{fn(store, ctx) as never}
				</ThreadContext.Provider>
			), el)
			return {
				update: next => setStore({ ...next }),
				dispose,
			}
		},
	}
}

export const SeamHost: Component<{
	component: SeamComponent
	props: SeamProps
	baseCtx: PluginContext
	class?: string
}> = (p) => {
	let container!: HTMLDivElement
	const rawThread = useThread()
	onMount(() => {
		let target: HTMLElement = container
		if ((p.component.styleIsolation ?? 'shadow') === 'shadow') {
			const shadowRoot = container.attachShadow({ mode: 'open' })
			target = document.createElement('div')
			shadowRoot.appendChild(target)
		}
		const ctx = deriveMountCtx(p.baseCtx, rawThread)
		const handle = p.component.mount(target, { ...p.props }, ctx)
		// Reactive prop updates cross the seam as plain update() calls
		createEffect(() => {
			handle.update?.({ ...p.props })
		})
		onCleanup(() => handle.dispose())
	})
	return <div class={p.class} data-seam={p.component.name} ref={container} />
}
